import { COLORS, FONTS, SPACINGS } from "@constants";
import { IconName } from "@tsTypes/icons";
import { Size } from "@tsTypes/ui";
import { ReactNode } from "react";
import styled from "styled-components";
import Icon from "../Icon";
import Tooltip from "../Tooltips/Tooltip";
import { getBadgeIconSize } from "./badgeUtils";

export interface BadgeProps {
  label: ReactNode;
  variant?: BadgeVariant | false;
  size: Extract<Size, "2xs" | "xs" | "sm" | "md" | "lg" | "xl">;
  leftIconName?: IconName;
  rightIconName?: IconName;
  margin?: string | 0;
  tooltipContent?: ReactNode;
}

export type BadgeVariant =
  | "green"
  | "orange"
  | "red"
  | "blue"
  | "light-grey"
  | "inverse"
  | "ghost"
  | "outline";

/**
 * Figma: https://cutt.ly/uilibrary-badge
 */
const Badge = ({
  label,
  variant: _variant,
  size,
  leftIconName,
  rightIconName,
  margin,
  tooltipContent,
}: BadgeProps) => {
  // variant can be false to allow for using && to dynamically set non-default values
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const variant = _variant || "default";

  let contentColor = COLORS.BLACK;
  switch (variant) {
    case "green": {
      contentColor = COLORS.GREEN_800;
      break;
    }
    case "orange": {
      contentColor = COLORS.ORANGE_900;
      break;
    }
    case "red": {
      contentColor = COLORS.RED_700;
      break;
    }
    case "blue": {
      contentColor = COLORS.BLUE_600;
      break;
    }
    case "light-grey": {
      contentColor = COLORS.NEUTRAL_600;
      break;
    }
    case "inverse": {
      contentColor = COLORS.WHITE;
      break;
    }
  }

  const iconSize = getBadgeIconSize(size);

  const badge = (
    <Container variant={variant} size={size} contentColor={contentColor} margin={margin}>
      {leftIconName && <Icon name={leftIconName} color={contentColor} size={iconSize} />}
      {label}
      {rightIconName && <Icon name={rightIconName} color={contentColor} size={iconSize} />}
    </Container>
  );

  return tooltipContent ? (
    <Tooltip asPortal position="top" content={tooltipContent}>
      {badge}
    </Tooltip>
  ) : (
    badge
  );
};

export default Badge;

const Container = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  ${({ margin }) => margin && `margin: ${margin};`}
  border-radius: 4px;
  color: ${({ contentColor }) => contentColor};

  ${({ variant }) => {
    switch (variant) {
      case "default": {
        return `
          background-color: ${COLORS.NEUTRAL_100};
        `;
      }
      case "green": {
        return `
          background-color: ${COLORS.GREEN_100};
        `;
      }
      case "orange": {
        return `
          background-color: ${COLORS.ORANGE_100};
        `;
      }
      case "red": {
        return `
          background-color: ${COLORS.RED_100};
        `;
      }
      case "blue": {
        return `
          background-color: ${COLORS.BLUE_LIGHT_300};
        `;
      }
      case "light-grey": {
        return `
          background-color: ${COLORS.NEUTRAL_50};
        `;
      }
      case "inverse": {
        return `
          background-color: ${COLORS.BLACK};
        `;
      }
      case "ghost": {
        return `
          background-color: transparent;
        `;
      }
      case "outline": {
        return `
          background-color: ${COLORS.WHITE};
          border: 1px solid ${COLORS.NEUTRAL_250};
        `;
      }
    }
  }}

  ${({ size }) => {
    switch (size) {
      case "2xs": {
        return `
          gap: ${SPACINGS.SIZE_0_5};
          height: 16px;
          padding: 0 ${SPACINGS.SIZE_1_5};
          ${FONTS.MEDIUM_4}
        `;
      }
      case "xs": {
        return `
          gap: ${SPACINGS.SIZE_0_5};
          height: 20px;
          padding: 0 ${SPACINGS.SIZE_1_5};
          ${FONTS.MEDIUM_3}
        `;
      }
      case "sm": {
        return `
          gap: ${SPACINGS.SIZE_1};
          height: 24px;
          padding: 0 ${SPACINGS.SIZE_2};
          ${FONTS.MEDIUM_3}
        `;
      }
      case "md": {
        return `
          gap: ${SPACINGS.SIZE_1};
          height: 32px;
          padding: 0 ${SPACINGS.SIZE_2};
          ${FONTS.MEDIUM_2}
        `;
      }
      case "lg": {
        return `
          gap: ${SPACINGS.SIZE_1};
          height: 40px;
          padding: 0 ${SPACINGS.SIZE_3};
          ${FONTS.MEDIUM_1}
        `;
      }
      case "xl": {
        return `
          gap: ${SPACINGS.SIZE_1};
          height: 48px;
          padding: 0 ${SPACINGS.SIZE_4};
          ${FONTS.MEDIUM_1}
        `;
      }
    }
  }}
`;
